import { TributeItem } from "c8-tributejs";
import Aspera, { HstDataTransfer } from "csi/comm/Aspera";
import html from "csi/html";
import { validateFiles } from "csi/service/fileService";
import { routerStore } from "csi/state/rootStore";
import { IFileData } from "csi/typings";
import { purifyForHtml } from "csi/utility/htmlUtility";
import { getLocationForUrl } from "csi/utility/routerUtility";
import { useLocale } from "designSystem/locale";
import { useUserProvider } from "designSystem/user";
import { FroalaEditor as FroalaEditorType, RegisterCommand } from "froala-editor";
import React, { RefObject, forwardRef, useEffect, useState } from "react";
import FroalaEditor, { MyComponentProps } from "react-froala-wysiwyg";
import FroalaDialog from "./FroalaDialog";
import { BUTTON_ATTACH, BUTTON_OPEN_IN_NEW, ButtonIcon, commandFont, commandFormat, commandOther, defaultConfig } from "./config";
import tributeMapping, { AT_TAG, MENTION_USER_URL, MentionUser } from "./tributeService";
import useMentionUser from "./useMentionUser";

export const LINK_CONTEXT_ID = "CENTRIC-CONTEXT";

/** set signature with "Sent from ${node}", and an hr above */
export const appendSignatureAndMoveCursorToStart = (reactEditorWrapper: FroalaEditor, sentFromText: string, sentFromLocation: string, linkHref: string): void => {
	const editor: FroalaEditorType = reactEditorWrapper.getEditor();
	const { html, link } = editor;
	html.insert("<br /><br /><hr style=\"width: 90px; margin: 0;\" />");
	html.insert(`<span style="line-height: 18px;">${sentFromText} </span>`);
	link.insert("DUMMY", sentFromLocation, { href: linkHref, [LINK_CONTEXT_ID]: true });
	moveCursorToStart(reactEditorWrapper);
};

export const disableEditorToolbar = (reactEditorWrapper: FroalaEditor): void => {
	const editor: FroalaEditorType = reactEditorWrapper.getEditor();
	editor.toolbar?.disable();
};

export const enableEditorToolbar = (reactEditorWrapper: FroalaEditor): void => {
	const editor: FroalaEditorType = reactEditorWrapper.getEditor();
	editor.toolbar?.enable();
};

/** process link to add href to the link, for the use case where user pastes the link into editor directly */
export const processLink = (html: string): string => {
	const el = document.createElement("html");
	el.innerHTML = html;
	const nodeList: HTMLCollectionOf<HTMLAnchorElement> = el.getElementsByTagName("a");

	for (const node of nodeList) {
		if (!node.href) {
			node.href = node.innerHTML;
		}
	}

	// the first child is "head", the second child is "body"
	return ((el.childNodes[1]) as HTMLElement).innerHTML;
};

/** get the content of editor */
export const getEditorContent = (reactEditorWrapper: FroalaEditor): string => {
	const originalContent = getFroalaEditor(reactEditorWrapper).html.get();
	let content = purifyForHtml(originalContent, {
		/** to support mentioning user */
		ADD_TAGS: [AT_TAG],

		/** to pass by the emails and for mention notifications server request */
		ADD_ATTR: [MENTION_USER_URL, LINK_CONTEXT_ID],
		ADD_URI_SAFE_ATTR: [MENTION_USER_URL],
	});

	content = processLink(content);

	return content;
};

/** set the content of editor */
export const setEditorContent = (reactEditorWrapper: FroalaEditor, content: string): void => {
	getFroalaEditor(reactEditorWrapper).html.set(html.sanitize(content));
};

/** get the element container of editor */
export const getFroalaContainer = (reactEditorWrapper: FroalaEditor): HTMLDivElement => {
	const editor = getFroalaEditor(reactEditorWrapper);
	const editorEl: HTMLElement = editor.$oel[0];
	return editorEl.querySelector(".fr-view");
};

/** get the editor from the react wrapper */
export const getFroalaEditor = (reactEditorWrapper: FroalaEditor): FroalaEditorType => {
	return reactEditorWrapper.getEditor();
};

/** move current selection to end of editor */
export const moveCursorToEnd = (reactEditorWrapper: FroalaEditor): void => {
	const editor = getFroalaEditor(reactEditorWrapper);
	const { selection } = editor;
	const el = getFroalaContainer(reactEditorWrapper);

	// selection will be null if the editor's content is not ready to read yet, normally, it shouldn't be null, do this safe than sorry
	selection?.setAtEnd(el);
	selection?.restore();
};

/** move current selection to start of editor */
export const moveCursorToStart = (reactEditorWrapper: FroalaEditor): void => {
	const editor = getFroalaEditor(reactEditorWrapper);
	const { selection } = editor;
	const el = getFroalaContainer(reactEditorWrapper);

	// selection will be null if the editor's content is not ready to read yet, normally, it shouldn't be null, do this safe than sorry
	selection?.setAtStart((el));
	selection?.restore();
};

export const selectTemplate = (user: TributeItem<MentionUser>) => `<span contenteditable="false" class="fr-deletable fr-tribute" ${MENTION_USER_URL}="${user.original.url}"><a class="browse" href="` + getLocationForUrl(user.original.url) + `">@${user.original.fullName}</a></span>`;

export interface FroalaEditorComponentProps extends MyComponentProps {
	/** other properties */
	configProps?: object;

	/** the default value of the editor when loading the first time */
	defaultValue?: string;

	/** properties for dialog(the one when click "open in dialog") */
	dialogProps?: object;

	/** the max height of the editor */
	heightMax?: number;

	/** the min height of the editor */
	heightMin?: number;

	/** the configuration of more action */
	moreActionProps?: Record<string, any>;

	/** callback for attach button */
	onAttach?: (files: File[], hstData: HstDataTransfer) => void;

	/** call back for error process when attaching files  */
	onAttachError?: (errorFileNames: string[]) => void;

	/** call back for when content changes */
	onModelChangeCallback?: (content: string) => void;

	/** pull files from modal dialog editors */
	pullFiles?: (addedFiles: IFileData[], deletedFiles: IFileData[], asperaData?: HstDataTransfer[]) => void;

	/** push the files to the modal dialog editor  */
	pushFilesPreOpenDialog?: () => IFileData[];

	/** localization resource for title when it's in dialog model */
	titleRes?: string;

	/** the content of the editor */
	value?: string;

	/** the width of the editor */
	width?: number;
}

const FroalaEditorComponent = forwardRef((props: FroalaEditorComponentProps, ref: RefObject<FroalaEditor>) => {
	const {
		configProps,
		defaultValue,
		dialogProps,
		heightMax,
		heightMin,
		moreActionProps,
		onAttach,
		onAttachError,
		onModelChangeCallback,
		pushFilesPreOpenDialog,
		pullFiles,
		titleRes,
		value,
		width,
	} = props;
	const loc = useLocale();
	const userProvider = useUserProvider();
	const [content, setContent] = useState<string>(value ?? defaultValue ?? "");

	RegisterCommand(BUTTON_ATTACH, {
		callback: async () => {
			if (Aspera.isEnabled()) {
				const data = await Aspera.selectFile();
				onAttach?.(null, data);
			} else {
				const files = await html.selectFiles();
				try {
					await validateFiles(files);
					onAttach?.(files, null);
				} catch (errorFileNames) {
					onAttachError?.(errorFileNames);
				}
			}
		},
		focus: true,
		icon: ButtonIcon.attach,
		title: loc.resource("CommentAttach"),
		undo: false,
	});

	const [setUserSearchQuery, setUserCallback] = useMentionUser([], userProvider.searchWithCache);

	const _moveCursorToEnd = () => {
		const { current } = ref;
		if ((content || defaultValue) && current) {
			moveCursorToEnd(current);
		}
	};

	// sync up with the content
	const pullContent = (content: string, addedFiles: IFileData[], deletedFiles: IFileData[], asperaData?: HstDataTransfer[]) => {
		setContent(content);
		pullFiles?.(addedFiles, deletedFiles, asperaData);

		// do this after setContent finish, make sure the text is there 
		setTimeout(_moveCursorToEnd, 0);
	};

	RegisterCommand(BUTTON_OPEN_IN_NEW, {
		callback: () => {
			routerStore.openDialog(FroalaDialog, {
				editorProps: {
					value: content,
					...dialogProps,
				},
				defaultFiles: pushFilesPreOpenDialog?.() ?? [],
				onCancel: _moveCursorToEnd,
				placeholderText: configProps["placeholderText"],
				pushContent: pullContent,
				titleRes,
				userProvider,
			});
		},
		focus: false,
		icon: ButtonIcon.openInNew,
		title: loc.resource("OpenInDialog"),
		undo: false,
	});

	const initializeEditor = () => {
		userProvider.clearSearchCache();
		tributeMapping(ref, setUserSearchQuery, setUserCallback, selectTemplate, loc);
	};

	const handleEditOn = () => {
		requestAnimationFrame(_moveCursorToEnd);
	};

	const handleModelChange = (content) => {
		onModelChangeCallback?.(content);
		setContent(content);
	};

	// do this so that it'll reflect the most up-to-date content once default value changes
	useEffect(() => {
		if (!!defaultValue) {
			setContent(defaultValue);

			// setContent is async, need timeout to let it finish first so that the text would be there
			setTimeout(_moveCursorToEnd, 0);
		}
	}, [defaultValue]);

	return (
		<FroalaEditor
			config={{
				...defaultConfig,
				heightMax: heightMax ?? 204,
				heightMin: heightMin ?? 204,
				toolbarButtons: {
					moreMisc: {
						buttons: [...commandFont, BUTTON_OPEN_IN_NEW, ...commandFormat, ...commandOther],
						buttonsVisible: 5,
					},

					// placeholder, will be rendered at the very end of the first line in the toolbar
					moreText: {
						buttons: [""],
						...moreActionProps,
						align: "right",
					},

					// have to specify this so that "paragraphFormat" and other buttons become available
					moreParagraph: {
						buttons: [],
					},
				},
				width: width ?? "auto",
				...configProps,
				events: {
					initialized: initializeEditor,
					"edit.on": handleEditOn,
					...configProps["events"],
				},
			}}
			model={content}
			onModelChange={handleModelChange}
			ref={ref}
		/>
	);
});

export default FroalaEditorComponent;